import React, { useCallback, useEffect, useRef, useState } from "react";
import { Flame, Info, Snowflake, Check, X, Ban, Timer } from "lucide-react";
import streaksService from "../../services/streaksService";
import { useFormat, useTranslation } from "../../i18n";
import en from "../../locales/en/widgets.json";
import WidgetShell from "./WidgetShell";
import Campfire from "./Campfire";
import { ENGAGEMENT_EVENT } from "../../utils/engagementEvents";

/**
 * The streak widget.
 *
 * The fire's height is this week's progress, so how close you are is something
 * you see before you read anything. What the fire cannot show is *what counts* —
 * that lives in the info card rather than in permanent body text, because it is
 * read once and then never again.
 *
 * Kept deliberately short: it shares a rail with two other widgets and all three
 * have to fit one screen.
 */

/**
 * English copy for the exported helpers when no `t` is supplied — tests, and
 * callers outside a component. It reads the same catalog the runtime bundles,
 * so the fallback cannot drift from what the widget renders.
 */
const englishT = (key, vars = {}) => {
  const found = key
    .split(".")
    .reduce((node, part) => (node == null ? undefined : node[part]), en);
  return String(found === undefined ? key : found).replace(
    /\{\{\s*(\w+)\s*\}\}/g,
    (match, name) =>
      vars[name] === undefined || vars[name] === null ? match : String(vars[name])
  );
};

const DOT_STATE = {
  hit: { className: "bg-orange-500 border-orange-500", titleKey: "streaks.history.hit" },
  freeze: { className: "bg-sky-100 border-sky-400", titleKey: "streaks.history.freeze" },
  miss: { className: "bg-transparent border-gray-300", titleKey: "streaks.history.miss" },
  pending: {
    className: "bg-transparent border-orange-400 border-dashed",
    titleKey: "streaks.history.pending",
  },
};

/**
 * The one line of body copy.
 *
 * It says what to *do*, not how far you are from a number: the progress bar and
 * the `n/m` counter beside it already carry the arithmetic, and a sentence that
 * repeats them ("4 more to keep it burning") spends the only line the widget
 * has on information you can already see. What no other element says is that
 * a streak is a thing you can lose, and that any interaction stops that.
 */
export function progressCopy(total, threshold, streak = 0, t = englishT) {
  if (threshold > 0 && total >= threshold) return t("streaks.progress.secured");
  if (total > 0) return t("streaks.progress.keep_going");
  if (streak > 0) return t("streaks.progress.keep_alive");
  return t("streaks.progress.start");
}

/**
 * Time left in the week, as a deadline you can act on.
 *
 * Coarse on purpose — days and hours, then hours and minutes in the last day.
 * A ticking second counter on a seven-day window is theatre, and it forces a
 * re-render every second to say nothing new.
 */
export function formatRemaining(ms, t = englishT) {
  if (ms == null || ms <= 0) return null;
  const minutes = Math.floor(ms / 60000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  if (days > 0) return t("streaks.remaining.days", { days, hours });
  if (hours > 0) return t("streaks.remaining.hours", { hours, minutes: minutes % 60 });
  return t("streaks.remaining.minutes", { minutes: Math.max(1, minutes) });
}

/** Milliseconds until the org's week closes, re-read on a slow tick. */
function useMsLeft(endsAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!endsAt) return undefined;
    // 20s keeps the minutes honest without a per-second render.
    const id = setInterval(() => setNow(Date.now()), 20000);
    return () => clearInterval(id);
  }, [endsAt]);
  if (!endsAt) return null;
  const end = Date.parse(endsAt);
  if (Number.isNaN(end)) return null;
  return end - now;
}

// `action` is the verb phrase, because "leave a recognition" reads wrong and
// one shared verb across all three would have to be that wrong for two of them.
// Both are catalog keys; the copy lives in widgets.json.
const KIND_LABEL = {
  reaction: { many: "streaks.maxed.kinds.reaction", action: "streaks.maxed.actions.reaction" },
  comment: { many: "streaks.maxed.kinds.comment", action: "streaks.maxed.actions.comment" },
  recognition: {
    many: "streaks.maxed.kinds.recognition",
    action: "streaks.maxed.actions.recognition",
  },
};

/**
 * Whether one more action of this kind would actually earn anything.
 *
 * Computed on the client from the caps the server already sends, so the widget
 * can react the instant you click instead of after a round trip. The server
 * stays the authority — this only decides which of two animations to play.
 */
export function willEarn(kind, weights = {}, caps = {}, earned = {}) {
  const weight = Number(weights[kind] || 0);
  if (weight <= 0) return false;
  const cap = Number(caps[kind] || 0);
  if (!cap) return true;
  return Number(earned[kind] || 0) < cap;
}

/** Kinds that have hit their weekly ceiling and can no longer add anything. */
export function maxedKinds(weights = {}, caps = {}, earned = {}) {
  return Object.keys(KIND_LABEL).filter(
    (kind) =>
      Number(weights[kind] || 0) > 0 &&
      Number(caps[kind] || 0) > 0 &&
      Number(earned[kind] || 0) >= Number(caps[kind])
  );
}

// The list shapes come from the catalog rather than a joined string, so a
// language that orders or punctuates a list differently can say so. There are
// only three kinds, so a list is never longer than three names or two actions.
function nameList(names, t) {
  if (names.length === 1) return names[0];
  if (names.length === 2) return t("streaks.maxed.names_two", { a: names[0], b: names[1] });
  return t("streaks.maxed.names_three", { a: names[0], b: names[1], c: names[2] });
}

function actionList(actions, t) {
  if (actions.length === 1) return actions[0];
  return t("streaks.maxed.actions_two", { a: actions[0], b: actions[1] });
}

export function maxedCopy(kinds, t = englishT) {
  if (!kinds.length) return null;
  const listed = nameList(
    kinds.map((k) => t(KIND_LABEL[k].many)),
    t
  );
  const subject = listed.charAt(0).toUpperCase() + listed.slice(1);
  const remaining = Object.keys(KIND_LABEL).filter((k) => !kinds.includes(k));
  if (!remaining.length) {
    return t("streaks.maxed.all_capped", { subject });
  }
  const suggestion = actionList(
    remaining.map((k) => t(KIND_LABEL[k].action)),
    t
  );
  return t("streaks.maxed.some_capped", { subject, suggestion });
}

function InfoCard({ weights, caps, threshold, label, onClose }) {
  const { t } = useTranslation("widgets");
  const fmt = useFormat();
  const rows = [
    { kind: "comment", label: t("streaks.info.kinds.comment"), value: weights.comment, cap: caps.comment },
    {
      kind: "recognition",
      label: t("streaks.info.kinds.recognition"),
      value: weights.recognition,
      cap: caps.recognition,
    },
    { kind: "reaction", label: t("streaks.info.kinds.reaction"), value: weights.reaction, cap: caps.reaction },
  ].filter((row) => row.value > 0);

  return (
    <div className="absolute inset-x-2 top-2 z-20 rounded-xl border border-gray-200 bg-white shadow-lg p-3">
      <div className="flex items-start gap-2">
        <p className="text-xs font-semibold text-gray-900 flex-1">
          {t("streaks.info.title", { threshold: fmt.number(threshold), label })}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common:actions.close")}
          className="text-gray-400 hover:text-gray-700 -mt-0.5"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <table className="w-full mt-2 text-xs">
        <tbody>
          {rows.map((row) => (
            <tr key={row.kind}>
              <td className="py-0.5 text-gray-700">{row.label}</td>
              <td className="py-0.5 text-right font-medium text-gray-900 tabular-nums">
                {fmt.number(row.value)}
              </td>
              <td className="py-0.5 pl-2 text-right text-gray-500 tabular-nums">
                {t("streaks.info.max", { cap: fmt.number(row.cap) })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="text-[11px] text-gray-500 mt-2">
        {t("streaks.info.caps_note")}
      </p>
    </div>
  );
}

export default function StreaksWidget({ compact = false }) {
  const { t } = useTranslation("widgets");
  const fmt = useFormat();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stoked, setStoked] = useState(false);
  const [stokeId, setStokeId] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  // The flame springing up from a cold fire. Distinct from a stoke: that says
  // "this counted", this says "the week has started".
  const [igniting, setIgniting] = useState(false);
  // The floating "+N" over the flame, and the flash on the maxed-out line. The
  // "+N" carries its own id rather than reusing stokeId, because a stoke can
  // fire again while the number is still on screen — which remounted it
  // mid-flight and replayed the animation instead of leaving it to finish.
  const [gain, setGain] = useState(null);
  const gainSeq = useRef(0);
  const [capFlash, setCapFlash] = useState(false);
  const previousTotal = useRef(null);
  const previousLit = useRef(null);
  // When the widget last flared on its own guess, so the backstop below can
  // tell the rise it already animated from one it has not seen.
  const optimisticAt = useRef(0);
  const igniteTimer = useRef(null);
  // Read inside the event handler, which must not re-subscribe on every fetch.
  const latest = useRef(null);
  latest.current = state;

  const refresh = useCallback(
    () =>
      streaksService
        .getMyStreak()
        .then((data) => {
          setState(data);
          return data;
        })
        .catch(() => null)
        .finally(() => setLoading(false)),
    []
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Commenting, reacting and recognising all earn credit, so re-read as soon as
  // one happens — otherwise the flare would only ever be seen on a page load,
  // which is exactly when it means nothing.
  useEffect(() => {
    const timers = [];
    const onAction = (event) => {
      const kind = event?.detail?.kind;
      const current = latest.current;

      // Answer the click immediately. Waiting for the round trip put a third of
      // a second between the action and any sign it registered, which is long
      // enough to read as "nothing happened" — the whole complaint the flare
      // exists to answer. The caps are already on the client, so which of the
      // two answers to give is knowable without asking the server.
      if (current && kind) {
        if (willEarn(kind, current.weights, current.caps, current.earned)) {
          const amount = Number((current.weights || {})[kind] || 0);
          gainSeq.current += 1;
          optimisticAt.current = Date.now();
          setStokeId((id) => id + 1);
          setStoked(true);
          setGain({ id: gainSeq.current, amount });
          timers.push(setTimeout(() => setStoked(false), 1000));
          timers.push(setTimeout(() => setGain(null), 1600));

          // First earn of the week: light the fire on the click rather than on
          // the response. Claiming the transition here — by moving previousLit
          // forward — is what stops the effect below from playing the catch a
          // second time when the refresh confirms it half a second later.
          if (!current.current_week_total && previousLit.current === false) {
            previousLit.current = true;
            setIgniting(true);
            clearTimeout(igniteTimer.current);
            igniteTimer.current = setTimeout(() => setIgniting(false), 1100);
          }
        } else {
          // Earned nothing. Say so rather than letting the click look ignored.
          setCapFlash(true);
          timers.push(setTimeout(() => setCapFlash(false), 1800));
        }
      }

      // Small delay: the credit is minted from the row the action just created,
      // so re-reading instantly can race the write it is meant to observe. The
      // server remains the authority — this reconciles the optimistic guess.
      timers.push(setTimeout(() => refresh(), 400));
    };
    window.addEventListener(ENGAGEMENT_EVENT, onAction);
    return () => {
      window.removeEventListener(ENGAGEMENT_EVENT, onAction);
      timers.forEach(clearTimeout);
    };
  }, [refresh]);

  // Backstop for a rise this widget did not see coming — credit earned in
  // another tab, or an action whose event never fired. Never on first render:
  // a flare on every page load stops meaning anything.
  useEffect(() => {
    const total = state?.current_week_total;
    if (total == null) return undefined;
    const previous = previousTotal.current;
    previousTotal.current = total;
    if (previous == null || total <= previous) return undefined;
    // The refresh that follows an action lands here reporting the rise the
    // optimistic path has already animated. Flaring again played the whole
    // thing twice, half a second apart.
    if (Date.now() - optimisticAt.current < 2500) return undefined;
    setStokeId((id) => id + 1);
    setStoked(true);
    const timer = setTimeout(() => setStoked(false), 1000);
    return () => clearTimeout(timer);
  }, [state?.current_week_total]);

  // The fire catching. Only on a real unlit → lit transition, so arriving at a
  // week already under way does not replay it.
  //
  // The timeout is held in a ref rather than cleared by the effect's own
  // cleanup: this fires at most once, so a re-run before it lands would cancel
  // the reset without ever re-arming it, and the class would stick.
  useEffect(() => {
    const total = state?.current_week_total;
    if (total == null) return;
    const isLit = total > 0;
    const previous = previousLit.current;
    previousLit.current = isLit;
    if (previous !== false || !isLit) return;
    setIgniting(true);
    clearTimeout(igniteTimer.current);
    igniteTimer.current = setTimeout(() => setIgniting(false), 1100);
  }, [state?.current_week_total]);

  useEffect(() => () => clearTimeout(igniteTimer.current), []);

  // Before the early returns: hooks cannot be conditional.
  const msLeft = useMsLeft(state?.week_ends_at);

  if (loading) {
    return (
      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex justify-center">
        <div className="animate-spin w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full" />
      </section>
    );
  }

  if (!state || state.enabled === false) return null;

  const label = state.currency_label || t("streaks.default_currency");
  const total = state.current_week_total || 0;
  const threshold = state.threshold || 0;
  const secured = threshold > 0 && total >= threshold;
  const intensity = threshold > 0 ? Math.min(1, total / threshold) : 0;
  const streak = state.current_streak || 0;
  const history = state.history || [];
  const maxed = maxedKinds(state.weights, state.caps, state.earned);
  const maxedMessage = maxedCopy(maxed, t);
  // Nothing earned this week means a cold fire, whatever the streak says. The
  // flame is this week's progress, and an unbroken streak with an empty week is
  // exactly the state the widget most needs to look alarming.
  const lit = total > 0;
  const timeLeft = formatRemaining(msLeft, t);
  // Under a day, with the week not yet earned, the deadline stops being
  // background information.
  const urgent = !secured && msLeft != null && msLeft < 24 * 3600 * 1000;

  return (
    <WidgetShell
      widgetKey="streaks"
      icon={Flame}
      title={t("streaks.title")}
      accentClass="text-orange-500"
      compact={compact}
      defaultCollapsed={compact}
      scroll={false}
      headerExtra={
        <>
          {timeLeft && (
            <span
              title={
                secured
                  ? t("streaks.deadline.secured", { time: timeLeft })
                  : t("streaks.deadline.left", { time: timeLeft })
              }
              className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums flex-shrink-0 ${
                urgent
                  ? "bg-red-50 text-red-700"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              <Timer className="w-3 h-3" />
              {timeLeft}
            </span>
          )}
          <button
            type="button"
            onClick={() => setShowInfo((v) => !v)}
            aria-label={t("streaks.how_it_works")}
            aria-expanded={showInfo}
            className="text-gray-400 hover:text-orange-600 transition-colors flex-shrink-0"
          >
            <Info className="w-4 h-4" />
          </button>
        </>
      }
    >
      <div className="relative px-3 py-3 bg-gradient-to-b from-orange-50/50 to-transparent">
        {showInfo && (
          <InfoCard
            weights={state.weights || {}}
            caps={state.caps || {}}
            threshold={threshold}
            label={label}
            onClose={() => setShowInfo(false)}
          />
        )}

        <div className="flex items-center gap-1">
          <div className="relative flex-shrink-0">
            <Campfire
              intensity={intensity}
              lit={lit}
              igniting={igniting}
              stoked={stoked}
              stokeId={stokeId}
              size={84}
            />
            {/* Names the number the action just added. The sparks say
                "something happened"; this says what. */}
            {gain && (
              <span
                key={gain.id}
                className="streak-gain pointer-events-none absolute left-1/2 top-2 -translate-x-1/2 rounded-full bg-orange-500 px-2 py-0.5 text-[11px] font-bold text-white shadow"
              >
                {t("streaks.gain", { amount: fmt.number(gain.amount) })}
              </span>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-bold text-gray-900 leading-none">
                {fmt.number(streak)}
              </span>
              <span className="text-xs font-semibold text-gray-500">
                {t("streaks.weeks_unit", { count: streak })}
              </span>
              {state.best_streak > 0 && (
                <span className="ml-auto text-[11px] text-gray-400 flex-shrink-0">
                  {t("streaks.best", { value: fmt.number(state.best_streak) })}
                </span>
              )}
            </div>

            <div className="mt-2 h-1.5 bg-gray-200/70 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-[width] duration-500"
                style={{ width: `${Math.max(4, intensity * 100)}%` }}
              />
            </div>

            <div className="flex items-baseline justify-between gap-2 mt-1.5">
              {secured ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-700">
                  <Check className="w-3.5 h-3.5" />
                  {progressCopy(total, threshold, streak, t)}
                </span>
              ) : (
                <span
                  className={`text-xs font-medium truncate ${
                    urgent ? "text-red-700" : "text-gray-700"
                  }`}
                >
                  {progressCopy(total, threshold, streak, t)}
                </span>
              )}
              <span className="text-[11px] text-gray-400 tabular-nums flex-shrink-0">
                {t("streaks.counter", {
                  total: fmt.number(total),
                  threshold: fmt.number(threshold),
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Why a click stopped counting. Permanent while the cap holds rather
            than a toast, because the answer is still true next time you try. */}
        {maxedMessage && (
          <p
            aria-live="polite"
            className={`mt-2 flex items-start gap-1.5 rounded-lg px-2 py-1.5 text-[11px] transition-colors duration-300 ${
              capFlash
                ? "bg-amber-100 text-amber-900 ring-1 ring-amber-300"
                : "bg-gray-50 text-gray-500"
            }`}
          >
            <Ban className="mt-px h-3 w-3 flex-shrink-0" />
            <span>{maxedMessage}</span>
          </p>
        )}

        {history.length > 0 && (
          <div className="flex items-center gap-1 mt-2.5 pt-2.5 border-t border-orange-100/70">
            {history.map((week) => {
              const meta = DOT_STATE[week.state] || DOT_STATE.miss;
              // week_start is a date-only ISO string; parse it as local time so
              // the tooltip does not slip a day west of UTC.
              return (
                <span
                  key={week.week_start}
                  title={t("streaks.history.week_of", {
                    date: fmt.date(`${week.week_start}T00:00:00`) || week.week_start,
                    state: t(meta.titleKey),
                  })}
                  className={`w-3 h-3 rounded-full border-2 ${meta.className}`}
                />
              );
            })}
            {state.freezes_used > 0 && (
              <Snowflake
                className="w-3 h-3 text-sky-500 ml-1"
                aria-label={t("streaks.history.freeze_used")}
              />
            )}
          </div>
        )}

        {state.reward?.awarded && (
          <p className="mt-2 text-[11px] font-medium text-purple-700">
            {t("streaks.reward_earned", { amount: fmt.number(state.reward.amount) })}
          </p>
        )}
      </div>
    </WidgetShell>
  );
}
